import {
  addToast,
  Button,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common/action-kit-imp';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { createStudentRiskActionAction } from './actions';

export type StudentRiskActionModalMethods = {
  open: () => void;
  close: () => void;
};

type StudentRiskActionModalProps = {
  sessionEnrollmentId: number | string;
  onSave?: () => void;
  groupTypeOptions?: SelectOptionType[];
  actionTypesOptions?: SelectOptionType[];
};

type FormFields = {
  groupType: SelectOptionType | null;
  actionType: SelectOptionType | null;
  owner: string;
  notes: string;
};

const DEFAULT_VALUES: FormFields = {
  groupType: null,
  actionType: null,
  owner: '',
  notes: '',
};

const StudentRiskActionModal = (
  {
    onSave,
    sessionEnrollmentId,
    actionTypesOptions = [],
    groupTypeOptions = [],
  }: StudentRiskActionModalProps,
  ref: Ref<StudentRiskActionModalMethods>,
) => {
  const { msgValidations } = useValidations();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_HISTORY,
  );
  const [visible, setVisible] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const [notesValue] = watch(['notes']);

  const notesCount = useMemo(() => {
    return notesValue?.length ?? 0;
  }, [notesValue]);

  const { action: handleClickSave, loading } = useActionKitImp(
    createStudentRiskActionAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`studentActionsModal.success`),
        });
        !!onSave && onSave();
        setVisible(false);
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`studentActionsModal.failed`),
          });
        },
      },
    },
  );

  const handleOpen = useCallback(() => {
    setVisible(true);
    reset(DEFAULT_VALUES);
  }, [reset]);

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading) {
        return;
      }
      handleClickSave({
        ...data,
        groupType: String(data.groupType?.value),
        actionType: String(data.actionType?.value),
        sessionEnrollmentId: String(sessionEnrollmentId),
      });
    },
    [loading, sessionEnrollmentId, handleClickSave],
  );

  if (!visible) {
    return <></>;
  }

  return (
    <Modal isOpen={true} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {t(`studentActionsModal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center">
              {t(`studentActionsModal.description`)}
            </p>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="groupType"
              options={groupTypeOptions}
              label={t(`studentActionsModal.groupType`)}
              isClearable={false}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="actionType"
              options={actionTypesOptions}
              label={t(`studentActionsModal.actionType`)}
              isClearable={false}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col xs={12} md={4}>
            <TextInput
              control={control}
              name="owner"
              label={t(`studentActionsModal.owner`)}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="notes"
              label={t(`studentActionsModal.notes`)}
              rules={{
                required: msgValidations.required,
              }}
              disabled={loading}
              maxLength={150}
            />
            <p>{`${notesCount} / 150`}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.cancel')}
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.add')}
              type="button"
              fullwidth
              onClick={handleSubmit(handleSave)}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(StudentRiskActionModal);
